import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MonitorPlay, RefreshCw } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { addDays, fmtHeure, todayIso } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { Panel } from "@/components/common/Panel";
import { ResumeBar } from "@/components/dashboard/ResumeBar";
import { LineCard } from "@/components/dashboard/LineCard";
import { AlertsPanel } from "@/components/dashboard/AlertsPanel";
import { InterventionsPanel } from "@/components/dashboard/InterventionsPanel";
import { TrendChart } from "@/components/dashboard/TrendChart";
import { ParQuartPanel } from "@/components/dashboard/ParQuartPanel";
import { ParetoPanel } from "@/components/dashboard/ParetoPanel";
import { cn } from "@/lib/utils";
import { fmtPct } from "@/lib/format";

const REFRESH_MS = 10000;

export default function DashboardPage() {
  const { entrepriseId, user } = useAuth();
  const [dateChoisie, setDateChoisie] = useState(null);   // null = « aujourd'hui » de l'usine (suit le passage à minuit)
  const aujourdhui = todayIso();
  const dateJour = dateChoisie || aujourdhui;
  const setDateJour = (d) => setDateChoisie(d && d < aujourdhui ? d : null);
  const [ligneId, setLigneId] = useState(null);
  const ready = user.role !== "superadmin" || !!entrepriseId;

  const dash = useQuery({ queryKey: ["dashboard", dateJour, entrepriseId], queryFn: () => get("/dashboard", { date_jour: dateJour }), refetchInterval: REFRESH_MS, enabled: ready });
  const rec = useQuery({ queryKey: ["corrections-recentes", entrepriseId], queryFn: () => get("/corrections/recentes"), refetchInterval: 60000, enabled: ready });
  const corriges = new Set((rec.data?.of || []).map((x) => x.of_id));
  const kpi = useQuery({ queryKey: ["kpi-jour", entrepriseId], queryFn: () => get("/of/kpi-jour"), refetchInterval: REFRESH_MS, enabled: ready });

  const lignesActives = (dash.data?.lignes || []).filter((l) => l.statut !== "hors_service");
  useEffect(() => { if (!ligneId && lignesActives.length) setLigneId(lignesActives[0].ligne_id); }, [lignesActives, ligneId]);

  const pertes = dash.data?.pertes || [];

  return (
    <div data-testid="dashboard-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Pilotage" }, { label: "Tableau de bord" }]} title="Tableau de bord décisionnel"
        subtitle={dash.data ? `État réel des lignes · rafraîchi toutes les ${REFRESH_MS / 1000} s · dernière mise à jour ${fmtHeure(new Date(dash.dataUpdatedAt).toISOString())}` : "Chargement…"}
        actions={<>
          <div className="flex items-center gap-1 text-[11px]">
            <span className="eyebrow mr-1 hidden sm:inline">Résumé du</span>
            <button data-testid="date-prev" className="h-7 w-7 border border-border rounded-sm hover:bg-muted font-mono" onClick={() => setDateJour(addDays(dateJour, -1))}>‹</button>
            <input data-testid="date-input" type="date" value={dateJour} max={aujourdhui} onChange={(e) => e.target.value && setDateJour(e.target.value)} className="h-7 bg-input border border-border rounded-sm px-2 font-mono text-[11px]" />
            <button data-testid="date-next" className="h-7 w-7 border border-border rounded-sm hover:bg-muted font-mono disabled:opacity-40" disabled={dateJour >= aujourdhui} onClick={() => setDateJour(addDays(dateJour, 1))}>›</button>
          </div>
          <Link to="/mur" data-testid="dashboard-mur-link" className="h-7 px-2 border border-border rounded-sm text-[11px] flex items-center gap-1 hover:bg-muted"><MonitorPlay className="h-3 w-3" />Mur d'écrans</Link>
          <button data-testid="dashboard-refresh" onClick={() => { dash.refetch(); kpi.refetch(); }} className="h-7 px-2 border border-border rounded-sm text-[11px] flex items-center gap-1 hover:bg-muted"><RefreshCw className={cn("h-3 w-3", dash.isFetching && "animate-spin")} />Actualiser</button>
        </>} />

      {!ready ? <EmptyState title="Sélectionnez une entreprise" hint="En tant que superadmin, choisissez l'entreprise à piloter dans la barre supérieure." />
        : dash.isLoading ? <LoadingState rows={6} /> : dash.error ? <ErrorState error={dash.error} onRetry={dash.refetch} /> : (<>
        <ResumeBar resume={dash.data.resume} kpiJour={kpi.data} />

        {dash.data.resume.action && (
          <div data-testid="dashboard-action" className="panel px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-l-2 border-l-primary">
            <span className="eyebrow">Action recommandée</span>
            <span className="font-medium">{dash.data.resume.action}</span>
            {pertes.length > 0 && <span className="ml-auto flex gap-3 font-mono text-[11px] text-muted-foreground">{pertes.map((p) => <span key={p.type}>Perte {p.type} <b className={cn(p.perte_pct > 20 ? "text-red-400" : p.perte_pct > 5 ? "text-amber-400" : "text-foreground")}>{fmtPct(p.perte_pct)}</b></span>)}</span>}
          </div>
        )}

        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="panel-title">État des lignes — temps réel ({dash.data.lignes.length})</h2>
            <div className="flex gap-3 text-[10px] font-mono text-muted-foreground">
              {[["bg-emerald-500", "Marche"], ["bg-red-500", "Arrêt non planifié"], ["bg-amber-500", "Arrêt planifié"], ["bg-sky-400", "Pause"], ["bg-slate-600", "Sans OF"]].map(([c, l]) => <span key={l} className="flex items-center gap-1"><i className={cn("h-2 w-2 rounded-full", c)} />{l}</span>)}
            </div>
          </div>
          {dash.data.lignes.length === 0 ? <Panel><EmptyState title="Aucune ligne configurée" hint="Créez des lignes de production dans Référentiels → Lignes." /></Panel>
            : <div data-testid="lines-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">{dash.data.lignes.map((l) => <LineCard key={l.ligne_id} line={l} corrige={!!l.of_actif && corriges.has(l.of_actif.id)} />)}</div>}
        </section>

        <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-3">
          <InterventionsPanel interventions={dash.data.interventions} />
          <AlertsPanel alertes={dash.data.alertes} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <TrendChart />
          <ParQuartPanel lignes={lignesActives} ligneId={ligneId} onLigneChange={setLigneId} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <ParetoPanel kind="arrets" du={addDays(dateJour, -6)} au={dateJour} />
          <ParetoPanel kind="rejets" du={addDays(dateJour, -6)} au={dateJour} />
        </div>
      </>)}
    </div>
  );
}
